import React, { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import ReactTooltip from 'react-tooltip';

import { Popup } from '@shared/ui';

import { StageChildItem } from './StageChildItem/StageChildItem';
import cls from './StageItem.module.css';
import { planFactHelper } from '../../../../../helpers/planFactHelper';
import { useEditStage } from '../../../../../hooks/useEditStage';
import { useShowBlock } from '../../../../../hooks/useShowBlock';
import { useUpdateAll } from '../../../../../hooks/useUpdateAll';
import { charterSelector } from '../../../../../store/reducers/charterSlice/charterSlice';
import { connectionActions } from '../../../../../store/reducers/connectionSlice/connectionSlice';
import { gantActions } from '../../../../../store/reducers/gantSlice/gantSlice';
import { positionSelector } from '../../../../../store/reducers/positionSlice/positionSlice';
import { stageSelector } from '../../../../../store/reducers/stageSlice/stageSlice';
import { ArrowBlock } from '../../../../UI/ArrowBlock/ArrowBlock';
import s from '../../../../UI/DateInput/DataInput.module.css';
import { DateInput } from '../../../../UI/DateInput/DateInput';
import { DayInput } from '../../../../UI/DayInput/DayInput';

export const StageItem = ({ stage, hintTarget }) => {
    const { opensStages } = useSelector((state) => state.gantReducer);
    if (!stage.positions.length) return null;

    const { resetStage, createStage, increaseStage, decreaseStage, onDataPickerStage } =
        useEditStage(stage);
    const dispatch = useDispatch();

    const { isNotEqualDate, isNotEqualDays, isRedDate, isRedDays } = planFactHelper(
        stage.planned,
        stage.actual,
        isPlanFact
    );

    const allPositions = useSelector(positionSelector.selectEntities);
    const allCharters = useSelector(charterSelector.selectEntities);
    const { settings } = useSelector((state) => state.gantReducer);
    const [stageForm, setStageForm] = useState({
        date: stage.planned.start_date ? moment(stage.planned.start_date).format() : null,
        days: stage.planned.days_count ? stage.planned.days_count : '',
    });

    const [distanceToLeft, setDistanceToLeft] = useState(0);

    const ref = useRef(null);

    useEffect(() => {
        if (ref.current) {
            const distance = $('#gant').offset();

            setDistanceToLeft(distance.left);
        }
    }, []);

    const { getStageChildren } = useUpdateAll(stage);

    const [isShowPopup, setState] = useState(false);

    const onChangePopup = (state, e) => {
        e.stopPropagation();
        if (isPlanFact) {
            setState(state);
        }
    };

    const { allPositions: allStagePosition } = getStageChildren(allPositions, stage);

    const stagePositions = [];
    stage.positions.forEach((stageChildGuid) => {
        if (allPositions[stageChildGuid]) {
            stagePositions.push(allPositions[stageChildGuid]);
        } else {
            allCharters[stageChildGuid].children.forEach((positionGuid) => {
                stagePositions.push(allPositions[positionGuid]);
            });
        }
    });

    const isNotNeedInitStage = stagePositions.some(
        (position) => position.planned.start_date !== null
    );

    const pickerEndDate = stageForm.date
        ? moment(stageForm.date)
              .add(+stageForm.days - 1, 'days')
              .format('DD.MM.YYYY')
        : moment(Date.now()).format('DD.MM.YYYY');

    useEffect(() => {
        setStageForm({
            date: stage.planned.start_date ? moment(stage.planned.start_date).format() : null,
            days: stage.planned.days_count ? stage.planned.days_count : '',
        });
    }, [stage]);

    let isDisabled = false;

    if (settings.isShowMHD) {
        isDisabled = !allStagePosition.length;
    } else {
        const laborPositions = allStagePosition.filter((position) => position.type === 'labor');

        isDisabled = !laborPositions.length;
    }

    const { isShow, onShow } = useShowBlock(
        `${stage.id}`,
        opensStages,
        (updatedState) => dispatch(gantActions.changeOpenStage(updatedState)),
        isDisabled
    );

    const onDateChange = (value) => {
        setStageForm((prevState) => ({ ...stageForm, days: value }));
    };

    const onSubmit = () => {
        if (stageForm.days === '0' || !stageForm.days) {
            resetStage(stage);
            return;
        }

        if (!isNotNeedInitStage) {
            createStage(stage, stageForm);
        } else {
            const newEndDate = moment(stageForm.date).add(+stageForm.days - 1, 'days');
            const oldEndDate = moment(stage.planned.end_date);
            const isDecrease = newEndDate.isBefore(oldEndDate);
            const differenceInDays = Math.abs(oldEndDate.diff(newEndDate, 'days'));

            if (isDecrease) {
                decreaseStage(stage, differenceInDays);
            } else {
                increaseStage(
                    stage,
                    {
                        days: stageForm.days,
                        date: moment(stageForm.date).format(),
                    },
                    differenceInDays
                );
            }
        }
    };

    const onPaymentDateChangeCancel = () => {
        resetStage(stage);
    };
    const onPaymentDateChange = ({ start, end }) => {
        const startDate = moment(start);
        const endDate = moment(end);
        const duration = moment.duration(endDate.diff(startDate));
        const days = `${Math.round(+duration.asDays())}`;

        if (!isNotNeedInitStage) {
            createStage(stage, { days, date: moment(start).format() });
        } else {
            onDataPickerStage(stage, { start, end, days });
        }
    };

    let isPlanFactShow = true;

    if (isPlanFact) {
        isPlanFactShow = isPlanFact && !!stage.planned.start_date;
    }

    if (!isPlanFactShow) {
        return null;
    }

    return (
        <>
            {!!isDisabled && (
                <ReactTooltip
                    id={`global-${stage.id}`}
                    aria-haspopup="true"
                    effect="solid"
                    delayHide={200}
                    delayShow={200}
                    type="light"
                    className={cls.tooltip}
                >
                    <p>{i18next.t('StageItem.return.ReactTooltip.part1')}</p>
                    <p>{i18next.t('StageItem.return.ReactTooltip.part2')}</p>
                    <p>{i18next.t('StageItem.return.ReactTooltip.part3')}</p>
                    <p>{i18next.t('StageItem.return.ReactTooltip.part4')}</p>
                </ReactTooltip>
            )}
            <div
                ref={ref}
                id="stage"
                data-tip
                data-for={`global-${stage.id}`}
                className={classNames(cls.wrapper)}
                {...(hintTarget ? { 'data-hint-target': hintTarget } : {})}
                onClick={() => {
                    onShow();
                    dispatch(connectionActions.endConnection());
                }}
            >
                <Popup
                    isOpen={isShowPopup}
                    contentText={i18next.t('StageItem.return.Popup.text')}
                    onHidePopup={() => setState(false)}
                    type="alert"
                />
                <div className={classNames(cls.StageItem, { [cls.factWrapper]: isPlanFact })}>
                    <div />
                    <ArrowBlock isShow={isShow} />
                    <div className={classNames(cls.title, { [cls.disabled]: isDisabled })}>
                        {stage.name}
                    </div>
                    <div className={classNames(cls.capacity)} />
                    <div
                        className={classNames(cls.dateBlock, { [cls.disabled]: isDisabled })}
                        onClick={(e) => onChangePopup(true, e)}
                    >
                        {isPlanFact ? (
                            <>
                                {stage.planned.start_date ? (
                                    <span className={classNames(cls.plannedDate)}>
                                        {moment(stage.planned.start_date).format(
                                            i18next.t('StageItem.return.date.format')
                                        )}
                                    </span>
                                ) : (
                                    <div className={classNames(s.svgWrapper)}>
                                        <svg
                                            width="20"
                                            height="20"
                                            viewBox="0 0 20 20"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path d="M16.25 5H3.25V17H16.25V5Z" stroke="#757575" />
                                            <path d="M7.25 3H5.25V7H7.25V3Z" stroke="#757575" />
                                            <path
                                                d="M16.5 8.75H3V9.25H16.5V8.75Z"
                                                stroke="#757575"
                                                strokeWidth="0.5"
                                            />
                                            <path d="M14.25 3H12.25V7H14.25V3Z" stroke="#757575" />
                                        </svg>
                                    </div>
                                )}
                            </>
                        ) : (
                            <DateInput
                                isDisabled={isDisabled}
                                key={Math.random()}
                                isRequired
                                date={stageForm.date}
                                parameterName="datePlan"
                                newCallbackNotSingle={onPaymentDateChange}
                                newCallbackNotSingleCancel={onPaymentDateChangeCancel}
                                options={{
                                    startDate: stageForm.date
                                        ? moment(stageForm.date).format(
                                              i18next.t('StageItem.return.date.format')
                                          )
                                        : moment(Date.now()).format(
                                              i18next.t('StageItem.return.date.format')
                                          ),
                                    endDate: pickerEndDate,
                                    autoUpdateInput: true,
                                    singleDatePicker: false,
                                    showDropdowns: true,
                                    autoApply: false,
                                }}
                                classes={[cls.stageInput, cls.input]}
                            />
                        )}

                        {isPlanFact && isNotEqualDate && (
                            <span className={classNames(cls.secondary, { [cls.red]: isRedDate })}>
                                {moment(stage.actual.start_date).format(
                                    i18next.t('StageItem.return.date.format')
                                )}
                            </span>
                        )}
                    </div>
                    <div
                        className={classNames(cls.daysBlock)}
                        onClick={(e) => onChangePopup(true, e)}
                    >
                        {isPlanFact ? (
                            <>
                                {stage.planned.days_count ? (
                                    <span>{stage.planned.days_count}</span>
                                ) : (
                                    <span style={{ color: '#ADB0B5' }}>0</span>
                                )}
                            </>
                        ) : (
                            <DayInput
                                classes={cls.stageInput}
                                value={stageForm.days}
                                onChange={onDateChange}
                                onSubmit={onSubmit}
                                isDisabled={isDisabled}
                            />
                        )}

                        {isPlanFact && isNotEqualDays && (
                            <span className={classNames(cls.secondary, { [cls.red]: isRedDays })}>
                                {stage.actual.days_count}
                            </span>
                        )}
                    </div>
                </div>
                {isShow && (
                    <div
                        className={classNames(cls.charterBlock)}
                        onClick={(event) => event.stopPropagation()}
                    >
                        {stage?.positions?.map((childGuid) => (
                            <StageChildItem key={childGuid} childGuid={childGuid} />
                        ))}
                    </div>
                )}
            </div>
        </>
    );
};
